import { ref } from 'vue'

import i18n from '@/libs/i18n'
import { toast } from '@/libs/toast'
import { useAppStore } from '@/stores/app'

/**
 * The house's ordering PIN on the guest's telephone (docs/PLAN_GOST.md, G3).
 *
 * Four digits the desk says at check-in, or the house sends by letter, and asks for
 * before every order and booking. It is never on the stay the portal reads — the link
 * is exactly what it keeps out — so this module only ASKS for it and hands it to one
 * press. Nothing here stores it: a second order asks again, which is what the house
 * chose.
 *
 * One window for the whole portal (`components/OrderPinModal.vue`, mounted once in
 * App.vue), opened from here: every screen that orders or books goes through
 * `withOrderPin`, so no screen can forget a refusal the others handle.
 */

/** The refusals the service answers a PIN with (pkg/enums/error: 141–143). */
const ORDER_PIN_REQUIRED = 141
const ORDER_PIN_WRONG    = 142
const ORDER_PIN_LOCKED   = 143

/** What the window is showing. `wrong` is the last four digits refused. */
export const orderPinPrompt = ref<{ open: boolean, wrong: boolean }>({ open: false, wrong: false })

let resolver: ((pin: string | null) => void) | null = null

/** Opens the window and answers the four digits, or null when the guest closed it. */
export const askOrderPin = (wrong: boolean): Promise<string | null> => {
    resolver?.(null)

    orderPinPrompt.value = { open: true, wrong }

    return new Promise((resolve) => { resolver = resolve })
}

/** The window's own answer — called by OrderPinModal.vue only. */
export const answerOrderPin = (pin: string | null) => {
    orderPinPrompt.value = { open: false, wrong: false }

    const resolve = resolver

    resolver = null
    resolve?.(pin)
}

/**
 * Which PIN refusal an answer is, if any. The code picks WHAT to ask the guest, never
 * whether a press may be retried — that stays with the operation verdict.
 */
const pinRefusal = (status: number, data: unknown): 'ask' | 'wrong' | 'locked' | null => {
    if (status !== 403 && status !== 429) return null

    const code = (data as { error?: { code?: number } } | null)?.error?.code

    if (code === ORDER_PIN_REQUIRED) return 'ask'
    if (code === ORDER_PIN_WRONG) return 'wrong'
    if (code === ORDER_PIN_LOCKED) return 'locked'

    return null
}

/**
 * One press that may need the PIN: asked FIRST where the stay says the house wants it,
 * asked again on a wrong one, and answered with the press's own answer — or null when
 * the guest closed the window or the stay is locked (the guest has then been told).
 *
 * `askFirst` is for a press the house may not guard at all — Milo's message card, say —
 * where asking up front would ask for nothing; the refusal still opens the window.
 */
export const withOrderPin = async <A extends { status: number, data: unknown }>(
    send: (pin: string) => Promise<A>,
    askFirst = true,
): Promise<A | null> => {
    let pin = ''

    if (askFirst && useAppStore().stay?.order_pin_required) {
        const typed = await askOrderPin(false)

        if (typed === null) return null

        pin = typed
    }

    for (;;) {
        const answer  = await send(pin)
        const refusal = pinRefusal(answer.status, answer.data)

        if (!refusal) return answer

        if (refusal === 'locked') {
            toast(i18n.global.t('order_pin.locked'))

            return null
        }

        const typed = await askOrderPin(refusal === 'wrong')

        if (typed === null) return null

        pin = typed
    }
}
